import type { ReactNode } from 'react';
import { Globe, KeyRound, Lock, PauseCircle, ShieldAlert, ShieldBan } from 'lucide-react';

import { Button, Spinner } from '../ui';
import { useT } from '../../i18n';
import { oidcEnabled, vuaXacThucTangCuong, xacThucTangCuong } from '../../lib/auth/oidc';
import { diaChiDung, maLoi } from '../../lib/errors';
import { usePhamVi } from '../../hooks/usePhamVi';
import { useNguCanhCuaToi } from '../../modules/auth/hooks/useNguCanhCuaToi';
import { VAI_TRO_QUAN_TRI_TENANT } from '../../modules/auth/nghiep-vu/quyen-cong-tenant';
import { useNguCanhNenTang } from '../../modules/vai-tro-nen-tang/hooks/useNguCanhNenTang';
import { useAuthStore } from '../../stores/auth';

/**
 * Cổng ngữ cảnh quyền: tài khoản đăng nhập thật chỉ thấy ứng dụng **sau khi**
 * có ngữ cảnh quyền của đúng cổng mình thuộc về.
 *
 * Đứng trong `AuthGate`, bọc cả khung lẫn thanh điều hướng: vẽ trước khi có
 * quyền là chớp `ForbiddenView` và một thanh điều hướng đủ 11 nhóm, rồi mới
 * thu về đúng gói.
 *
 * Hai cổng, hai endpoint — xem `lib/pham-vi-cong`:
 * - cổng tenant gọi `GET /api/v1/users/me/context` (ADR-0034), có `dich_vu`,
 *   `mods`, `tenant_status`;
 * - cổng nền tảng gọi `GET /api/v1/platform-context` (ADR-0074 mục 6). Trước
 *   ADR-0074, quyền của nhân sự nền tảng sinh bằng `if/else` ngay trong trình
 *   duyệt ở `lib/auth/oidc.ts`, nên không ai cấu hình được; giờ server tính.
 *
 * Không gọi khi chạy dữ liệu mẫu (chưa cấu hình Keycloak): tài khoản mẫu đã
 * mang sẵn quyền.
 *
 * Đã có ngữ cảnh mà lần tải lại sau đó lỗi thì giữ ngữ cảnh cũ, không chặn
 * người đang làm việc — trừ khi lỗi là tài khoản vừa bị khoá, hoặc tổ chức vừa
 * bị tạm ngưng (`403 TENANT_SUSPENDED` của Gateway). Từ ADR-0077 Gateway chặn
 * cả quản trị tổ chức, nên quản trị tổ chức cũng thấy màn chặn, với câu chỉ
 * đường riêng: chỉ nhà cung cấp dịch vụ mở lại được. Một API khác trả mã này
 * giữa phiên thì `providers.tsx` tải lại ngữ cảnh để màn chặn hiện ngay.
 *
 * Chặn theo IP (`403 IP_BLOCKED` / `IP_NOT_ALLOWED` của Gateway, ADR-0059 mục
 * 6.1) cũng vậy: lỗi đó chặn **mọi** lời gọi `/api`, kể cả lấy ngữ cảnh, nên
 * rơi vào nhánh lỗi chung thì người dùng đọc "thử lại sau ít phút" và bấm Thử
 * lại mãi. Màn chặn nói đúng lý do và cách xử lý (đổi mạng, liên hệ quản trị);
 * không có Thử lại vì chỉ đổi mạng hoặc đổi danh sách mới gỡ được. Blocklist
 * nền tảng áp cả nhóm route nền tảng, nên cổng nền tảng cũng có màn này.
 *
 * Xác thực tăng cường (`401 CAN_XAC_THUC_TANG_CUONG`, ADR-0090 mục 2): người dùng ở
 * ngoài dải tin cậy, tổ chức đòi OTP mà phiên chưa qua OTP. Màn mời "Xác thực bằng
 * mã OTP" chuyển sang Keycloak với `acr_values=otp` rồi quay lại đúng trang. Vừa
 * xác thực xong mà vẫn bị đòi thì đổi câu (cấu hình chưa xong, liên hệ quản trị)
 * thay vì mời bấm lại mãi.
 *
 * Sai địa chỉ truy cập (`403 SAI_DIA_CHI_TRUY_CAP`, ADR-0097): người dùng mở cổng qua domain thương hiệu của tổ
 * chức khác (hoặc domain đã tạm ngưng). Không phải lỗi quyền: màn nói địa chỉ không thuộc tổ chức của họ và đưa
 * liên kết về địa chỉ chung (`correct_base_url` của Gateway). Không có Thử lại vì vào lại cùng địa chỉ vẫn bị chặn.
 */
export function NguCanhGate({ children }: { children: ReactNode }) {
  const phamVi = usePhamVi();
  const laThat = oidcEnabled();
  const canTenant = laThat && phamVi === 'tenant';
  const canNenTang = laThat && phamVi === 'nen-tang';
  const qTenant = useNguCanhCuaToi(canTenant);
  const qNenTang = useNguCanhNenTang(canNenTang);
  const q = canNenTang ? qNenTang : qTenant;
  const laQuanTri = useAuthStore((s) => s.user?.vaiTroLop1?.includes(VAI_TRO_QUAN_TRI_TENANT) ?? false);

  if (!canTenant && !canNenTang) return <>{children}</>;
  if (maLoi(q.error) === 'USER_DISABLED') return <ManChan loai="biKhoa" />;
  if (maLoi(q.error) === 'TENANT_SUSPENDED') {
    return <ManChan loai={laQuanTri ? 'tamNgungQuanTri' : 'tamNgung'} />;
  }
  // 403 là câu trả lời DỨT KHOÁT của server, không phải trục trặc tạm: tài khoản không thuộc nhóm
  // của cổng đang mở. Mời "thử lại sau ít phút" ở đây là mời bấm mãi mãi — nói đúng việc cần làm
  // (đăng nhập bằng tài khoản khác) và chỉ chừa một lối ra.
  if (maLoi(q.error) === 'PERMISSION_DENIED') return <ManChan loai="khongThuocCong" />;
  if (maLoi(q.error) === 'IP_BLOCKED') return <ManChan loai={canNenTang ? 'ipBiChanNenTang' : 'ipBiChan'} />;
  if (maLoi(q.error) === 'IP_NOT_ALLOWED') return <ManChan loai="ipNgoaiDanhSach" />;
  if (maLoi(q.error) === 'SAI_DIA_CHI_TRUY_CAP') return <ManChan loai="saiDiaChi" diaChi={diaChiDung(q.error)} />;
  if (maLoi(q.error) === 'CAN_XAC_THUC_TANG_CUONG') {
    return <ManChan loai={vuaXacThucTangCuong() ? 'canOtpLai' : 'canOtp'} />;
  }
  if (q.data) return <>{children}</>;
  if (q.isError) {
    return (
      <ManChan
        loai={maLoi(q.error) === 'UNAUTHENTICATED' ? 'hetPhien' : 'loi'}
        thuLai={() => void q.refetch()}
        dangThuLai={q.isFetching}
      />
    );
  }
  return <DangTai />;
}

function DangTai() {
  const { t } = useT();
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-screen flex-col items-center justify-center gap-3 bg-subtle px-6 text-center"
    >
      <Spinner size="large" />
      <p className="text-sm text-content-secondary">{t('nguCanh.dangTai')}</p>
    </div>
  );
}

interface ManChanProps {
  loai:
    | 'biKhoa'
    | 'tamNgung'
    | 'tamNgungQuanTri'
    | 'hetPhien'
    | 'khongThuocCong'
    | 'ipBiChan'
    | 'ipBiChanNenTang'
    | 'ipNgoaiDanhSach'
    | 'canOtp'
    | 'canOtpLai'
    | 'saiDiaChi'
    | 'loi';
  /** Địa chỉ đúng để đưa người dùng về, chỉ với `saiDiaChi`. */
  diaChi?: string;
  thuLai?: () => void;
  dangThuLai?: boolean;
}

/**
 * Màn chặn cả ứng dụng, khuôn giống `ForbiddenView` nhưng luôn có lối ra là
 * đăng xuất: khung ứng dụng chưa vẽ nên không có menu tài khoản nào để bấm.
 */
function ManChan({ loai, diaChi, thuLai, dangThuLai = false }: ManChanProps) {
  const { t } = useT();
  const logout = useAuthStore((s) => s.logout);
  const laIp = loai === 'ipBiChan' || loai === 'ipBiChanNenTang' || loai === 'ipNgoaiDanhSach';
  const laOtp = loai === 'canOtp' || loai === 'canOtpLai';
  const laSaiDiaChi = loai === 'saiDiaChi';
  const tongNguy = loai === 'biKhoa' || laIp;

  /**
   * Lối ra **luôn đi được** của màn chặn.
   *
   * `logout` của Keycloak kết thúc phiên rồi mới chuyển trang; Keycloak không phản hồi thì người
   * dùng kẹt lại đúng màn này, không còn nút nào khác (khung ứng dụng chưa vẽ nên không có menu
   * tài khoản). Hẹn một nhịp: chưa rời trang thì tự về `/dang-nhap` kèm `da_dang_xuat=1`, tham số
   * đó chặn màn đăng nhập tự đẩy ngược sang Keycloak.
   */
  const thoat = () => {
    logout();
    window.setTimeout(() => {
      window.location.replace('/dang-nhap?da_dang_xuat=1');
    }, 2000);
  };

  const { tieuDe, moTa } = {
    biKhoa: { tieuDe: t('nguCanh.biKhoaTieuDe'), moTa: t('dangNhap.sso.bi_khoa') },
    tamNgung: { tieuDe: t('nguCanh.tamNgungTieuDe'), moTa: t('nguCanh.tamNgungMoTa') },
    tamNgungQuanTri: { tieuDe: t('nguCanh.tamNgungTieuDe'), moTa: t('nguCanh.tamNgungMoTaQuanTri') },
    hetPhien: { tieuDe: t('nguCanh.loiTieuDe'), moTa: t('loi.hetPhien') },
    khongThuocCong: {
      tieuDe: t('nguCanh.khongThuocCongTieuDe'),
      moTa: t('nguCanh.khongThuocCongMoTa'),
    },
    ipBiChan: { tieuDe: t('nguCanh.ipTieuDe'), moTa: t('nguCanh.ipBiChanMoTa') },
    ipBiChanNenTang: { tieuDe: t('nguCanh.ipTieuDe'), moTa: t('nguCanh.ipBiChanMoTaNenTang') },
    ipNgoaiDanhSach: { tieuDe: t('nguCanh.ipTieuDe'), moTa: t('nguCanh.ipNgoaiDanhSachMoTa') },
    canOtp: { tieuDe: t('nguCanh.canOtpTieuDe'), moTa: t('nguCanh.canOtpMoTa') },
    canOtpLai: { tieuDe: t('nguCanh.canOtpTieuDe'), moTa: t('nguCanh.canOtpLaiMoTa') },
    saiDiaChi: {
      tieuDe: t('nguCanh.saiDiaChiTieuDe'),
      moTa: t(diaChi ? 'nguCanh.saiDiaChiMoTa' : 'nguCanh.saiDiaChiMoTaKhongLink', {
        host: window.location.host,
      }),
    },
    loi: { tieuDe: t('nguCanh.loiTieuDe'), moTa: t('dangNhap.sso.khong_tra_duoc_quyen') },
  }[loai];

  return (
    <div
      role="alert"
      className="flex h-screen flex-col items-center justify-center bg-subtle px-6 text-center"
    >
      <div
        className={
          tongNguy
            ? 'mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-danger-50'
            : 'mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-warning-50'
        }
      >
        {loai === 'biKhoa' ? (
          <Lock size={32} className="text-danger-500" aria-hidden />
        ) : laOtp ? (
          <KeyRound size={32} className="text-warning-500" aria-hidden />
        ) : laIp ? (
          <ShieldBan size={32} className="text-danger-500" aria-hidden />
        ) : laSaiDiaChi ? (
          <Globe size={32} className="text-warning-500" aria-hidden />
        ) : loai === 'tamNgung' || loai === 'tamNgungQuanTri' ? (
          <PauseCircle size={32} className="text-warning-500" aria-hidden />
        ) : (
          <ShieldAlert size={32} className="text-warning-500" aria-hidden />
        )}
      </div>
      <h1 className="mb-2 text-xl font-bold text-content-primary">{tieuDe}</h1>
      <p className="mb-6 max-w-md text-sm text-content-secondary">{moTa}</p>
      <div className="flex flex-wrap justify-center gap-3">
        {loai === 'loi' && thuLai && (
          <Button onClick={thuLai} loading={dangThuLai}>
            {t('chung.thuLai')}
          </Button>
        )}
        {laOtp && (
          <Button
            variant={loai === 'canOtp' ? 'primary' : 'outline'}
            onClick={() => void xacThucTangCuong(`${window.location.pathname}${window.location.search}`)}
          >
            {t('nguCanh.nutXacThucOtp')}
          </Button>
        )}
        {laSaiDiaChi && diaChi && (
          <Button variant="primary" href={diaChi}>
            {t('nguCanh.nutVeDiaChiDung', { host: new URL(diaChi).host })}
          </Button>
        )}
        <Button
          variant={loai === 'loi' || loai === 'canOtp' || (laSaiDiaChi && diaChi) ? 'outline' : 'primary'}
          onClick={thoat}
        >
          {t('khung.dangXuat')}
        </Button>
      </div>
    </div>
  );
}
